import { differenceInMinutes, format, isValid, parse, parseISO } from 'date-fns'

import { DAY_MINUTES, parseTimeToMinutes } from './time'

export type AssistanceRequestType = 'ARR' | 'DEP'

export interface AssistanceTimingInput {
  type: AssistanceRequestType
  startTime: string
  endTime: string
  scheduledTrainTime?: string | null
  realtimeTrainTime?: string | null
  autoUpdateAssistanceJobTime?: boolean
}

export interface ResolvedAssistanceTiming {
  startTime: string
  endTime: string
  scheduledTime?: string
  realtimeTime?: string
  realtimeShiftMinutes: number
  isRealtimeAdjusted: boolean
}

export const extractTimeFromValue = (value?: string | null): string | undefined => {
  if (!value) return undefined

  if (value.includes('T')) {
    const isoDate = parseISO(value)
    if (isValid(isoDate)) {
      return format(isoDate, 'HH:mm')
    }
  }

  const parsedTime = parse(value, 'HH:mm', new Date())
  if (isValid(parsedTime)) {
    return format(parsedTime, 'HH:mm')
  }

  return undefined
}

const hasDatePart = (value?: string | null): value is string => Boolean(value?.includes('T'))

const getClockDeltaMinutes = (
  scheduledTrainTime?: string | null,
  realtimeTrainTime?: string | null
): number | null => {
  const scheduledMinutes = parseTimeToMinutes(extractTimeFromValue(scheduledTrainTime))
  const realtimeMinutes = parseTimeToMinutes(extractTimeFromValue(realtimeTrainTime))
  if (scheduledMinutes == null || realtimeMinutes == null) return null

  const rawDelta = realtimeMinutes - scheduledMinutes
  if (rawDelta > DAY_MINUTES / 2) return rawDelta - DAY_MINUTES
  if (rawDelta < -DAY_MINUTES / 2) return rawDelta + DAY_MINUTES
  return rawDelta
}

const getRealtimeShiftMinutes = (
  scheduledTrainTime?: string | null,
  realtimeTrainTime?: string | null
): number | null => {
  if (!scheduledTrainTime || !realtimeTrainTime) return null

  if (hasDatePart(scheduledTrainTime) && hasDatePart(realtimeTrainTime)) {
    const scheduledDate = parseISO(scheduledTrainTime)
    const realtimeDate = parseISO(realtimeTrainTime)
    if (isValid(scheduledDate) && isValid(realtimeDate)) {
      return differenceInMinutes(realtimeDate, scheduledDate)
    }
  }

  return getClockDeltaMinutes(scheduledTrainTime, realtimeTrainTime)
}

const minutesToHHmmWrapped = (minutes: number): string => {
  const wrappedMinutes = ((Math.round(minutes) % DAY_MINUTES) + DAY_MINUTES) % DAY_MINUTES
  const hour = Math.floor(wrappedMinutes / 60)
  const minute = wrappedMinutes % 60
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

export const resolveAssistanceTiming = ({
  startTime,
  endTime,
  scheduledTrainTime,
  realtimeTrainTime,
  autoUpdateAssistanceJobTime = false,
}: AssistanceTimingInput): ResolvedAssistanceTiming => {
  const normalizedStartTime = extractTimeFromValue(startTime) ?? startTime
  const normalizedEndTime = extractTimeFromValue(endTime) ?? endTime
  const scheduledTime = extractTimeFromValue(scheduledTrainTime)
  const realtimeTime = extractTimeFromValue(realtimeTrainTime)

  const baseTiming: ResolvedAssistanceTiming = {
    startTime: normalizedStartTime,
    endTime: normalizedEndTime,
    scheduledTime,
    realtimeTime,
    realtimeShiftMinutes: 0,
    isRealtimeAdjusted: false,
  }

  if (!autoUpdateAssistanceJobTime) return baseTiming

  // The API does not expose manual-adjustment provenance, so realtime movement
  // shifts the assistance window currently returned by the backend.
  const realtimeShiftMinutes = getRealtimeShiftMinutes(scheduledTrainTime, realtimeTrainTime)
  if (!realtimeShiftMinutes) return baseTiming

  const startMinutes = parseTimeToMinutes(normalizedStartTime)
  const endMinutes = parseTimeToMinutes(normalizedEndTime)
  if (startMinutes == null || endMinutes == null) return baseTiming

  const durationMinutes =
    endMinutes > startMinutes ? endMinutes - startMinutes : endMinutes + DAY_MINUTES - startMinutes
  if (durationMinutes <= 0) return baseTiming

  return {
    ...baseTiming,
    startTime: minutesToHHmmWrapped(startMinutes + realtimeShiftMinutes),
    endTime: minutesToHHmmWrapped(startMinutes + durationMinutes + realtimeShiftMinutes),
    realtimeShiftMinutes,
    isRealtimeAdjusted: true,
  }
}
